import type { PartyInfo, Address } from '@acitra/shared';

interface AddressCardProps {
  title: string;
  effective: PartyInfo | undefined;
  original: PartyInfo | undefined;
  onEdit: () => void;
}

function formatAddress(address: Address | undefined): string[] | null {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state, address.postalCode].filter(Boolean).join(', '),
    address.country,
  ].filter(Boolean) as string[];
  return parts.length > 0 ? parts : null;
}

export function AddressCard({ title, effective, original, onEdit }: AddressCardProps) {
  // Derive hasEdits by comparing object references
  const hasEdits = effective !== original;

  // Compare effective vs original to see which fields actually differ
  const nameChanged = hasEdits && (effective?.name || '') !== (original?.name || '');
  const effectiveAddr = effective?.address;
  const originalAddr = original?.address;
  const addressChanged =
    hasEdits &&
    ((effectiveAddr?.line1 || '') !== (originalAddr?.line1 || '') ||
      (effectiveAddr?.line2 || '') !== (originalAddr?.line2 || '') ||
      (effectiveAddr?.city || '') !== (originalAddr?.city || '') ||
      (effectiveAddr?.state || '') !== (originalAddr?.state || '') ||
      (effectiveAddr?.postalCode || '') !== (originalAddr?.postalCode || '') ||
      (effectiveAddr?.country || '') !== (originalAddr?.country || ''));

  return (
    <div className="bg-white shadow rounded-lg p-6 relative group">
      <button
        onClick={onEdit}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Edit ${title}`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      </button>
      <h2 className="text-lg font-medium text-gray-900 mb-4">{title}</h2>
      <div className="space-y-2">
        {effective?.name && (
          <p className={`font-medium ${nameChanged ? 'text-edited-600' : 'text-gray-900'}`}>
            {effective.name}
          </p>
        )}
        {formatAddress(effective?.address) ? (
          <div className={`text-sm ${addressChanged ? 'text-edited-600' : 'text-gray-600'}`}>
            {formatAddress(effective?.address)!.map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
